import { useEffect, useState } from 'react';
import { getPlayer } from './usePlayerInfo';
import { useQueryClient } from '@tanstack/react-query';
import { fetchJson, fetchPicksData, getLiveBonusPointsForFixture } from '../components/services/league-standings-services';
import type {
  LeagueStandingsAPI,
  LiveEvent,
  LivePlayer,
  EntryHistory,
  LiveComputedRow,
  Player
} from '../types';

export const useLeagueStandings = (leagueId: string | null) => {

  const [rows, setRows] = useState<LiveComputedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!leagueId) return;

    const run = async () => {
      try {
        setLoading(true);

        // Get original standings
        const league = await fetchJson<LeagueStandingsAPI>(`/api/league/${leagueId}/standings`);
        const members = league.standings.results;

        if (!members?.length) {
          setRows([]);
          setLoading(false);
          return;
        }

        // get current gw
        const bootstrap = await fetchJson<any>('/api/current-gameweek');
        const currentEvent: number = bootstrap.id;
        if (!currentEvent) throw new Error('Could not determine current event');

        // live player points for the current event
        const live = await fetchJson<LiveEvent>(`/api/event/${currentEvent}/livePlayerPoints`);
        const liveMap = new Map<number, LivePlayer['stats']>();

        const livePlayerBonusPoints = new Map<number, number>();
        for (const el of live.elements) {
          liveMap.set(el.id, el.stats);
        }

        // get current gw fixtures
        const currentGameweekFixtures = await fetchJson<any>(`/api/${currentEvent}/fixtures`);

        const fixturesStatus = new Map<number, object>();
        currentGameweekFixtures.forEach((fixture: any) => {
          fixturesStatus.set(fixture.id, { started: fixture.started, finished_provisional: fixture.finished_provisional, finished: fixture.finished });
          // Once a fixture is finished_provisional, the live stats' total_points already
          // include bonus (FPL settles it as soon as the match ends, well before the
          // official `finished` flag flips). Only estimate bonus ourselves while the
          // match is genuinely still in play, or we double-count it.
          if (!(fixture?.started && !fixture.finished_provisional)) return;

          getLiveBonusPointsForFixture(
            fixture.stats?.find((s: any) => s.identifier === "bps")
          )?.forEach((points, playerId) => {
            livePlayerBonusPoints.set(playerId, points);
          });
        })

        let hasCapGameFinishedCache = new Map<number, boolean>();

        // for each manager, get team selection and pre-GW season total
        const computed: LiveComputedRow[] = await Promise.all(
          members.map(async (m) => {
            const entryId = m.entry ?? m.id;

            const { picks, activeChip, transferCost } = await fetchPicksData(entryId, currentEvent);

            // Season history (to get pre-GW total)
            const history = await fetchJson<EntryHistory>(`/api/${entryId}/history`);
            const preGwTotal = (history.current || [])
              .filter((c) => c.event < currentEvent)
              .reduce((acc, c) => Math.max(acc, c.total_points), 0);

            // --- Compute LIVE GW points ---
            // Rules implemented:
            //  • Starting XI scores unless Bench Boost (then bench too)
            //  • Captain doubles; Triple Captain triples
            //  • If captain DNP (minutes=0), and VC played, switch to VC x2
            //  • We DO NOT perform full autosubs formation logic here (that requires post-minutes data for all XI).
            const isBenchBoost = activeChip === 'BBOOST' || activeChip === 'BENCH_BOOST';
            const cap = picks.find((p) => p.is_captain);
            const vice = picks.find((p) => p.is_vice_captain);

            const minutesOf = (playerId: number) => liveMap.get(playerId)?.minutes ?? 0;
            const pointsOf = (playerId: number) => liveMap.get(playerId)?.total_points ?? 0;

            // Determine if captain should flip to vice (only if cap minutes==0 and vice played)
            // keep capId possibly undefined, coerce/validate before using
            let capId: number | undefined;
            let capTeamId: number | undefined;
            let capName: string | undefined;
            if (cap?.element != null) {
              capId = Number(cap.element);
              if (!Number.isNaN(capId)) {
                // try read cached player first
                let captainData = queryClient.getQueryData<Player>(['player', capId]);
                if (!captainData) {
                  captainData = await queryClient.fetchQuery<Player>({
                    queryKey: ['player', capId],
                    queryFn: () => getPlayer(capId as number),
                    staleTime: Infinity, // Will change this to gameweek deadline 
                    gcTime: 1000 * 60 * 60 * 24 * 7, // keep a week in cache
                  });
                }
                capTeamId = captainData?.team;
                capName = captainData?.web_name;


              }
            }

            let capMultiplier = (cap?.multiplier ?? 1);
            let hasCapGameFinished: boolean | undefined;

            if (capId == null) {
              //   return;
            } else if (hasCapGameFinishedCache.has(capId)) {
              hasCapGameFinished = hasCapGameFinishedCache.get(capId);

            } else {
              const finished = hasCapGameFinished = currentGameweekFixtures
                .filter((fixture: any) => fixture.team_a === capTeamId || fixture.team_h === capTeamId)
                .some((fixture: any) => fixture.finished_provisional === true);
              hasCapGameFinishedCache.set(capId, finished);
            }

            if (cap?.element && minutesOf(cap.element) === 0 && hasCapGameFinished && vice?.element && minutesOf(vice.element) > 0) { //Here we need to only do this if Vice's game has finished
              capId = vice.element;
              capMultiplier = Math.max(2, capMultiplier);
            }

            // Sum starters plus bench if Bench Boost
            const eligiblePositions = isBenchBoost ? Array.from({ length: 15 }, (_, i) => i + 1) : Array.from({ length: 11 }, (_, i) => i + 1);

            let gwPoints = 0;
            for (const pick of picks) {
              if (!eligiblePositions.includes(pick.position)) continue;

              const liveBonus = livePlayerBonusPoints.get(pick.element) ?? 0;
              const base = pointsOf(pick.element) + liveBonus;
              let mult = 1;

              if (capId && pick.element === capId) {
                mult = capMultiplier >= 2 ? capMultiplier : 2;
              } else if (pick.is_vice_captain) {
                // VC only scores extra if it *became* cap; which we already handled above.
                mult = 1;
              } else if (pick.is_captain) {
                // Original captain that didn’t play -> 0 extra (already handled by flip)
                mult = minutesOf(pick.element) > 0 ? 2 : 1;
              } else {
                // normal player
                mult = pick.multiplier > 1 ? 1 : pick.multiplier;
              }

              gwPoints += base * mult;
            }

            const liveTotal = preGwTotal + gwPoints - (transferCost);

            return {
              ...m,
              event_total: gwPoints,
              total: liveTotal,
              live_event_points: gwPoints,
              live_total: liveTotal,
              live_rank: 0,
              captain_pick_name: capName || '',
              active_chip: activeChip || ''
            };
          })
        );

        // sort & assign live ranks 
        computed.sort((a, b) => b.live_total - a.live_total);
        let rankCounter = 0;
        let lastPoints = Number.NEGATIVE_INFINITY;
        let lastRank = 0;
        for (const row of computed) {
          rankCounter += 1;
          if (row.live_total !== lastPoints) {
            lastRank = rankCounter;
            lastPoints = row.live_total;
          }
          row.live_rank = lastRank;
        }

        setRows(computed);
        setLoading(false);
      } catch (e: any) {
        console.error(e);
        setError('Failed to compute live standings');
        setLoading(false);
      }
    };

    run();
  }, [leagueId, queryClient]);

  return { rows, loading, error };

}